import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { projectsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import SiteDetailModal from './SiteDetailModal';

interface Site {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  project: { id: string; name: string; code: string; city: { id: string; name: string; parentId?: string | null } };
  city?: { id: string; name: string; parentId?: string | null } | null;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { id: string; name: string; parentId?: string | null };
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
};

const SiteList: React.FC = () => {
  const { projectId } = useParams();
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [sites, setSites] = useState<Site[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    status: 'ACTIVE',
  });

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  const openDetailModal = (site: Site) => {
    setSelectedSite(site);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedSite(null);
  };

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, sitesRes] = await Promise.all([
        projectsAPI.getOne(projectId!),
        projectsAPI.getProjectSites(projectId!),
      ]);
      setProject(projectRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setFormData({ name: '', code: '', address: '', status: 'ACTIVE' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      await projectsAPI.createSite({
        ...formData,
        projectId: projectId!,
        address: formData.address || undefined,
      });
      toast.success('Şantiye oluşturuldu');
      toggleModal();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-sm-0">Şantiyeler</h4>
              {project && (
                <p className="text-muted mb-0">
                  {project.city.name} / {project.name} ({project.code})
                </p>
              )}
            </div>
            <div className="page-title-right d-flex gap-2">
              <Link to="/projects" className="btn btn-outline-secondary">
                <i className="ri-arrow-left-line me-1"></i>
                Projeler
              </Link>
              {isAdmin && (
                <Button color="primary" onClick={toggleModal}>
                  <i className="ri-add-line me-1"></i>
                  Yeni Şantiye
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Şantiye Listesi</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : sites.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">Bu projede henüz şantiye bulunmuyor.</p>
                    {isAdmin && (
                      <Button color="primary" onClick={toggleModal}>
                        İlk Şantiyeyi Ekle
                      </Button>
                    )}
                  </div>
                ) : isMobile ? (
                  // Mobile Card View
                  <div className="admin-card-list">
                    {sites.map((site) => (
                      <div key={site.id} className="admin-card-item">
                        {/* Card Header - Clickable */}
                        <div
                          className="admin-card-header"
                          onClick={() => openDetailModal(site)}
                          style={{ cursor: 'pointer' }}
                        >
                          <h6 className="admin-card-title">{site.name}</h6>
                          <span
                            className={`px-2 py-1 rounded-pill small ${statusStyles[site.status]}`}
                            style={{ fontWeight: 500 }}
                          >
                            {statusLabels[site.status]}
                          </span>
                        </div>

                        {/* Card Body - Clickable */}
                        <div
                          className="admin-card-body"
                          onClick={() => openDetailModal(site)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-barcode-line"></i>
                              Kod
                            </span>
                            <div className="admin-info-value value-highlight">
                              {site.code}
                            </div>
                          </div>
                          {site.address && (
                            <div className="admin-info-row">
                              <span className="admin-info-label">
                                <i className="ri-map-pin-line"></i>
                                Adres
                              </span>
                              <span className="admin-info-value">
                                {site.address}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Card Footer */}
                        <div className="admin-card-footer">
                          <Link
                            to={`/sites/${site.id}/works`}
                            className="btn btn-sm btn-outline-primary"
                            style={{ flex: 1 }}
                          >
                            <i className="ri-tools-line me-1"></i>
                            İşler
                          </Link>
                          <Link
                            to={`/sites/${site.id}/personnel`}
                            className="btn btn-sm btn-outline-warning"
                            style={{ flex: 1 }}
                          >
                            <i className="ri-team-line me-1"></i>
                            Personel
                          </Link>
                        </div>
                        <div className="admin-card-footer" style={{ borderTop: 'none', paddingTop: 0 }}>
                          <Link
                            to={`/daily-records?site=${site.id}`}
                            className="btn btn-sm btn-outline-info"
                            style={{ flex: 1 }}
                          >
                            <i className="ri-file-list-3-line me-1"></i>
                            Kayıtlar
                          </Link>
                          <Link
                            to={`/waybills?site=${site.id}`}
                            className="btn btn-sm btn-outline-success"
                            style={{ flex: 1 }}
                          >
                            <i className="ri-article-line me-1"></i>
                            İrsaliyeler
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop Table View
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Şantiye Kodu</th>
                          <th>Şantiye Adı</th>
                          <th>Adres</th>
                          <th>Durum</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sites.map((site) => (
                          <tr
                            key={site.id}
                            onClick={() => openDetailModal(site)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <strong>{site.code}</strong>
                            </td>
                            <td>{site.name}</td>
                            <td>{site.address || '-'}</td>
                            <td>
                              <span
                                className={`px-2 py-1 rounded-pill small ${statusStyles[site.status]}`}
                                style={{ fontWeight: 500 }}
                              >
                                {statusLabels[site.status]}
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <Link
                                to={`/sites/${site.id}/works`}
                                className="btn btn-sm btn-outline-primary me-1"
                              >
                                <i className="ri-tools-line me-1"></i>
                                İşler
                              </Link>
                              <Link
                                to={`/sites/${site.id}/personnel`}
                                className="btn btn-sm btn-outline-warning me-1"
                              >
                                <i className="ri-team-line me-1"></i>
                                Personel
                              </Link>
                              <Link
                                to={`/daily-records?site=${site.id}`}
                                className="btn btn-sm btn-outline-info me-1"
                              >
                                Kayıtlar
                              </Link>
                              <Link
                                to={`/waybills?site=${site.id}`}
                                className="btn btn-sm btn-outline-success"
                              >
                                İrsaliyeler
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

      {/* Yeni Şantiye Modal */}
      <AdaptiveModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title="Yeni Şantiye"
        size="md"
        footer={
          <>
            <Button color="secondary" onClick={toggleModal}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="site-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="site-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Şantiye Kodu *</Label>
            <Input
              type="text"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toUpperCase() })
              }
              placeholder="Örn: S-001"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Şantiye Adı *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Şantiye adını girin"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Adres</Label>
            <Input
              type="textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Şantiye adresi (opsiyonel)"
              rows={2}
            />
          </FormGroup>
          <FormGroup>
            <Label>Durum</Label>
            <Input
              type="select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="ACTIVE">Aktif</option>
              <option value="SUSPENDED">Askıda</option>
            </Input>
          </FormGroup>
        </Form>
      </AdaptiveModal>

      {/* Site Detail Modal */}
      <SiteDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        site={selectedSite}
        onUpdate={fetchData}
      />
    </>
  );
};

export default SiteList;
